import { useCallback, useEffect, useMemo } from 'react';
import { useFeatureValue } from '@growthbook/growthbook-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { FEATURE_FLAG } from '../constants/featureFlags';
import { useModal } from './useDialog';
import { useGeoLocation } from './useGeoLocation';
import useAuthForms from './useAuthForms';
import useLocalization from './useLocalization';
import { usePromoCode } from './usePromoCode';
import { useRegister } from '../mutations/useRegister';
import { useMarketplaceConfig } from '../queries/marketplace';
import { getEnabledFields } from '../utils';
import type { SelectOption } from '../components/molecules/form-elements/FormSelectField';
import {
  GEO_LOCATION_FIELDS,
  getAvailableRegisterFields,
  getRegisterSchema,
  type RegisterFormData,
  type RegisterFormProps,
} from './registerForm.shared';

export const useRegisterFormController = ({ promoCode }: RegisterFormProps = {}) => {
  const { t } = useLocalization();
  const { closeModal } = useModal();
  const { openLoginForm } = useAuthForms();
  const { promoCode: storedPromoCode, clearPromoCode } = usePromoCode();
  const { mutateAsync: registerUser, invalidPromoCode } = useRegister(closeModal);
  const prefilledPromoCode = promoCode ?? storedPromoCode ?? '';
  const isPromoLocked = !!prefilledPromoCode && !invalidPromoCode;

  const { data: config } = useMarketplaceConfig();
  const { signUpFormFields = [] } = config?.marketplaceConfig?.registrationFlow || {};
  const enabledFields = getEnabledFields(signUpFormFields);

  const allCurrencyOptions = useFeatureValue<SelectOption[]>(FEATURE_FLAG.CURRENCY_OPTIONS, []);
  const marketplaceCurrencyOptions = useMemo(
    () => allCurrencyOptions.filter((currency) => currency.value === config?.marketplaceConfig?.currency),
    [allCurrencyOptions, config]
  );
  const registerSchema = useMemo(() => getRegisterSchema(t, signUpFormFields), [signUpFormFields, t]);

  const hasGeoFields = GEO_LOCATION_FIELDS.some((field) => enabledFields.includes(field));
  const { data: geoData } = useGeoLocation(hasGeoFields);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    getValues,
    setValue,
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      email: undefined,
      password: undefined,
      firstName: undefined,
      lastName: undefined,
      phone: undefined,
      address: undefined,
      city: undefined,
      region: undefined,
      country: undefined,
      code: prefilledPromoCode || undefined,
      zip: undefined,
      currency: config?.marketplaceConfig?.currency,
      ageVerified: false,
      dateOfBirth: undefined,
    },
  });

  const availableFields = useMemo(
    () =>
      getAvailableRegisterFields({
        t,
        marketplaceCurrencyOptions,
        prefilledPromoCode,
        isPromoLocked,
      }),
    [isPromoLocked, marketplaceCurrencyOptions, prefilledPromoCode, t]
  );

  useEffect(() => {
    if (!geoData) return;

    GEO_LOCATION_FIELDS.forEach((field) => {
      if (geoData[field] && enabledFields.includes(field) && getValues(field) === undefined) {
        setValue(field, geoData[field], { shouldDirty: true });
      }
    });
  }, [enabledFields, geoData, getValues, setValue]);

  const handleClose = useCallback(() => {
    reset();
    closeModal();
  }, [closeModal, reset]);

  const fieldsToRender = useMemo(
    () =>
      availableFields
        .filter((field) => enabledFields.includes(field.name))
        .sort((a, b) => {
          const orderA = signUpFormFields.find((currentField) => currentField.name === a.name)?.order ?? 0;
          const orderB = signUpFormFields.find((currentField) => currentField.name === b.name)?.order ?? 0;

          return orderA - orderB;
        }),
    [availableFields, enabledFields, signUpFormFields]
  );

  const submit = useCallback(
    async (data: RegisterFormData) => {
      const { ageVerified, ...registerData } = data;

      const payload = Object.fromEntries(
        Object.entries(registerData).map(([key, value]) => [key, value === '' ? undefined : value])
      );

      try {
        await registerUser(payload);
        clearPromoCode();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      }
    },
    [clearPromoCode, registerUser]
  );

  return {
    t,
    control,
    errors,
    isSubmitting,
    getValues,
    setValue,
    fieldsToRender,
    handleClose,
    onSubmit: handleSubmit(submit),
    submit,
    openLoginForm,
  };
};
